import React, { forwardRef, useState } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    leftIcon, 
    rightIcon, 
    fullWidth = false, 
    className = '', 
    onFocus,
    onBlur,
    ...props 
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    
    const baseInputClasses = `
      w-full px-4 py-3 rounded-xl
      bg-gray-900/50 backdrop-blur-md
      border transition-all duration-300
      text-white placeholder-gray-500
      focus:outline-none
    `;
    
    const stateClasses = error 
      ? 'border-red-500/50 focus:border-red-400 focus:ring-2 focus:ring-red-500/20' 
      : `border-gray-700/50 hover:border-gray-600/70 
         focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20
         focus:shadow-lg focus:shadow-primary-500/10`;
    
    const paddingLeftClass = leftIcon ? 'pl-11' : '';
    const paddingRightClass = rightIcon ? 'pr-11' : '';
    
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };
    
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };
    
    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label 
            className={`
              block text-sm font-medium mb-2 transition-colors duration-300
              ${isFocused ? 'text-primary-400' : 'text-gray-300'}
              ${error ? 'text-red-400' : ''}
            `}
          >
            {label}
          </label>
        )}
        
        <div className="relative group">
          {/* Glow effect on focus */}
          <div 
            className={`
              absolute -inset-0.5 rounded-xl opacity-0 blur-sm transition-opacity duration-300
              ${isFocused && !error ? 'opacity-50' : ''}
            `}
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 242, 0.3), rgba(0, 31, 89, 0.5))'
            }}
          />
          
          {leftIcon && (
            <div 
              className={`
                absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none
                transition-colors duration-300
                ${isFocused ? 'text-primary-400' : 'text-gray-500'}
              `}
            >
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            className={`
              ${baseInputClasses}
              ${stateClasses}
              ${paddingLeftClass}
              ${paddingRightClass}
              ${className}
              relative
            `.replace(/\s+/g, ' ').trim()}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          
          {rightIcon && (
            <div 
              className={`
                absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none
                transition-colors duration-300
                ${isFocused ? 'text-primary-400' : 'text-gray-500'}
              `}
            >
              {rightIcon}
            </div>
          )}
        </div>
        
        {(error || helperText) && (
          <p 
            className={`
              mt-2 text-sm transition-colors duration-300
              ${error ? 'text-red-400' : 'text-gray-500'}
            `}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
